<template>
  <div ref="elementRef" class="relative grid h-full">
    <base-picture
      v-if="responsiveMedia.images"
      v-bind="responsiveMedia.images"
      class="full grid-area-stack"
      fit="cover"
      :lazy
      :props-img="{ class: 'h-full' }"
    />
    <vf-video
      v-else-if="responsiveMedia.video"
      v-bind="responsiveMedia.video"
      ref="videoRef"
      :autoplay="!lazy"
      class="full grid-area-stack"
      fit="cover"
      loop
      :meta="responsiveMedia.video.sm"
      muted
    />
    <base-link
      v-if="mediaLink"
      aria-hidden="true"
      class="absolute-0"
      :data-cta="mediaLink.delocalizedTracking"
      tabindex="-1"
      :target="mediaLink.targetAttribute"
      :to="mediaLink.url"
      @click="handlePromotionClick('img')"
    />
    <cms-base-gradient
      v-if="gradientStyle && gradientStyle !== 'off'"
      v-bind="{ gradientTone, gradientStyle, gradientBreakpoints }"
      :alignment="gradientAlignment[contentAlignment]"
      :position="gradientPosition[contentAlignment]"
    />
    <div class="pointer-events-none relative grid-area-stack">
      <div class="h-full f-col" :class="alignment[contentAlignment]">
        <div
          v-style:bg="backdrop"
          :class="[
            variantContentClass[variant] || variantContentClass.default,
            spacing,
          ]"
        >
          <h2
            v-if="title"
            v-style:c="titleColor"
            class="pointer-within"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-banner-title"
          >
            <base-link
              v-if="mediaLink"
              :data-cta="mediaLink.delocalizedTracking"
              :target="mediaLink.targetAttribute"
              :to="mediaLink.url"
              @click="handlePromotionClick('title')"
            >
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </base-link>
            <template v-else>
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </template>
          </h2>
          <p
            v-if="subtitle"
            v-style:c="subtitleColor"
            :class="subtitleStyle"
            data-test-id="cms-banner-text"
          >
            <span v-if="$feature.showTitleHtml" v-html="subtitleHtml" />
            <span v-else>{{ subtitle }}</span>
          </p>
          <div
            v-if="targets.length"
            class="pointer-within f-col gap-4 md:flex-row md:wrap"
            :class="[buttons, { 'justify-center': centerButtonAlignment.includes(contentAlignment) }]"
          >
            <cms-shared-button
              v-for="(target, i) in targets"
              :key="i"
              v-bind="{ ...target, ...getTextLinkStyles(content) }"
              :size
              @click="handlePromotionClick(`cta${i + 1}`)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type { BannerEspotAlignment, BannerEspotContent } from '#types/components/cms/banner-espot'
import type { ButtonSize } from '#types/components/vf/button'
import { SectionContextKey } from '#content/contexts/section'

const { content } = defineProps<{
  content: BannerEspotContent
}>()

const {
  backdrop,
  contentAlignment,
  gradientBreakpoints,
  gradientStyle,
  gradientTone,
  hideTitle,
  media,
  mediaTarget,
  name,
  promotionTracking,
  subtitle,
  subtitleColor,
  subtitleHtml,
  subtitleStyle,
  targets = [],
  title,
  titleColor,
  titleHtml,
  titleStyle,
  variant,
} = content

const { getMedia } = useCms()
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)
const {
  brandClasses: {
    container: {
      spacing,
      variants: variantContentClass
    },
    buttons
  }
} = useAppConfig().components.cms.bannerEspot

const { lazy } = inject(SectionContextKey, {} as SectionProvider)
const videoRef = ref()
const responsiveMedia = getMedia(media)
const size = { sm: { size: 'tiny' as ButtonSize } }

const mediaLink = mediaTarget || targets[0]

const alignment: Partial<Record<BannerEspotAlignment, string>> = {
  'bottom': 'text-center justify-end items-center',
  'center': 'text-center center',
  'left': 'justify-center items-start',
  'top': 'text-center items-center',
  'bottom-left': 'justify-end items-start'
}

const centerButtonAlignment = ['bottom', 'center', 'top']

const gradientPosition: Record<BannerEspotAlignment, 'top' | 'bottom' | 'center'> = {
  'bottom': 'bottom',
  'center': 'center',
  'left': 'center',
  'top': 'top',
  'bottom-left': 'bottom'
}

const gradientAlignment: Record<BannerEspotAlignment, 'left' | 'right' | 'center'> = {
  'bottom': 'center',
  'center': 'center',
  'left': 'left',
  'top': 'center',
  'bottom-left': 'left'
}

onMounted(() => videoRef.value?.play().catch((e) => log.error(e)))
</script>
